// Main service exports and initialization
import { BlogService } from './BlogService.js';
import { AuthorService } from './AuthorService.js';
import { TagService } from './TagService.js';

// Singleton instances for the application
export const blogService = new BlogService();
export const authorService = new AuthorService();
export const tagService = new TagService();

// Export service classes for custom instantiation if needed
export { BlogService, AuthorService, TagService };

// Export DAO classes for direct access if needed
export { BlogDAO } from './dao/BlogDAO.js';
export { AuthorDAO } from './dao/AuthorDAO.js';
export { TagDAO } from './dao/TagDAO.js';
export { BaseDAO } from './dao/BaseDAO.js';

// Service registry for dependency injection or service discovery
export const serviceRegistry = {
  blog: blogService,
  author: authorService,
  tag: tagService
};

// Initialize all services (call this in your app startup)
export async function initializeServices() {
  try {
    console.log('🚀 Initializing blog services...');
    
    // Pre-warm critical caches
    await Promise.all([
      blogService.blogDAO.getBlogDatabase(),
      authorService.authorDAO.getAuthorDatabase(),
      tagService.tagDAO.getAllTags()
    ]);
    
    console.log('✅ Blog services initialized successfully');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize blog services:', error);
    return false;
  }
}

// Clear all service caches (useful for development/testing)
export function clearAllCaches() {
  blogService.clearCache();
  authorService.authorDAO.clearCache();
  tagService.tagDAO.clearCache();
  console.log('🧹 All service caches cleared');
}

// Health check for all services
export async function healthCheck() {
  const results = {
    blog: { healthy: false, error: null },
    author: { healthy: false, error: null },
    tag: { healthy: false, error: null }
  };

  try {
    // Test blog service
    const stats = await blogService.getStats();
    results.blog.healthy = stats.totalPosts >= 0;
  } catch (error) {
    results.blog.error = error.message;
  }

  try {
    // Test author service
    const authors = await authorService.getAllAuthors({ limit: 1 });
    results.author.healthy = Array.isArray(authors);
  } catch (error) {
    results.author.error = error.message;
  }

  try {
    // Test tag service
    const tags = await tagService.getAllTags({ limit: 1 });
    results.tag.healthy = Array.isArray(tags);
  } catch (error) {
    results.tag.error = error.message;
  }

  return results;
}

// Service configuration
export const serviceConfig = {
  cache: {
    defaultTTL: 10 * 60 * 1000, // 10 minutes
    authorTTL: 15 * 60 * 1000,  // 15 minutes
    blogTTL: 10 * 60 * 1000,    // 10 minutes
    tagTTL: 10 * 60 * 1000      // 10 minutes
  },
  pagination: {
    defaultLimit: 10,
    maxLimit: 100
  },
  search: {
    minQueryLength: 2,
    maxResults: 50
  }
};

// Helper function to get all services
export function getAllServices() {
  return serviceRegistry;
}

// Default export for convenience
export default {
  blog: blogService,
  author: authorService,
  tag: tagService,
  initialize: initializeServices,
  clearCaches: clearAllCaches,
  healthCheck,
  config: serviceConfig
};